import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BsReverseLayoutTextSidebarReverse } from 'react-icons/bs';
import { IoColorPaletteSharp, IoLogOut, IoFootsteps } from 'react-icons/io5';
import { FaSmileBeam } from 'react-icons/fa';
import { FaUserTie } from 'react-icons/fa6';
import { MdArrowDropDownCircle, MdCelebration } from 'react-icons/md';
import { ImSearch } from 'react-icons/im';
import { RxCross2 } from 'react-icons/rx';
import { RiUserSettingsFill } from 'react-icons/ri';
import { TbLayoutSidebarLeftCollapse, TbLayoutSidebarLeftCollapseFilled } from 'react-icons/tb';
import { TiArrowRepeatOutline } from 'react-icons/ti';
import { BsKeyboard } from 'react-icons/bs';
import './WalkthroughTour.css';

/* ───────────────────────────────────────────────────────────
   Step definitions
   target: data-tour attribute value (null = centred modal)
   ─────────────────────────────────────────────────────────── */
const ShortcutKey = ({ keys, action }) => (
  <button
    className="wt-shortcut-btn"
    onClick={(e) => {
      e.stopPropagation();
      if (action) action();
    }}
    title={`Shortcut: ${keys.join(' + ')}`}
  >
    {keys.map((key, i) => (
      <React.Fragment key={key}>
        <kbd>{key}</kbd>
        {i < keys.length - 1 && <span>+</span>}
      </React.Fragment>
    ))}
  </button>
);

const STEPS = [
  {
    target: null,
    icon: FaSmileBeam,
    fallback: '👋',
    title: 'Welcome to the Platform!',
    desc: (
      <>
        This quick walkthrough will guide you through the main interface so you can get started in
        no time.
        <div className="wt-hint-box">
          <div className="wt-hint-item">
            <ShortcutKey keys={['➡']} /> Next Step
          </div>
          <div className="wt-hint-item">
            <ShortcutKey keys={['⬅']} /> Previous Step
          </div>
          <div className="wt-hint-item">
            <ShortcutKey keys={['Esc']} /> Skip Tour
          </div>
        </div>
      </>
    ),
  },
  {
    target: 'restart-btn',
    icon: TiArrowRepeatOutline,
    fallback: '🔄',
    title: 'Walkthrough Replay',
    desc: (
      <>
        See this button in the corner? You can click it anytime to restart this walkthrough whenever you need a refresher.
        <div className="wt-shortcut-group">
          <ShortcutKey keys={['Shift', 'W']} action={() => document.querySelector('[data-tour="restart-btn"]')?.click()} />
          <span>Replay Walkthrough</span>
        </div>
      </>
    ),
  },
  {
    target: 'user-identity',
    icon: FaUserTie,
    fallback: '👤',
    title: 'Your Identity',
    desc: "This section shows your name and your current role. It helps you confirm you're logged in with the right account.",
  },
  {
    target: 'main-content',
    icon: BsReverseLayoutTextSidebarReverse,
    fallback: '📊',
    title: 'Main Content Area',
    desc: 'This is where all your page content and dashboards are displayed. Each section you navigate to will render here.',
  },
  {
    target: 'global-search',
    icon: ImSearch,
    fallback: '🔍',
    title: 'Global Navigation Search',
    desc: (
      <>
        Quickly find any section of the platform. Just type what you need and the sidebar will
        instantly filter down to the matched pages.
        <div className="wt-shortcut-group">
          <ShortcutKey keys={['Shift', 'S']} action={() => document.querySelector('.sidebar-search-input')?.focus()} />
          <span>Toggle Search</span>
        </div>
      </>
    ),
  },
  {
    target: 'sidebar-nav',
    icon: TbLayoutSidebarLeftCollapseFilled,
    fallback: '🧭',
    title: 'Sidebar Navigation',
    desc: 'The sidebar contains all the main navigation links for the platform. Use it to jump between different sections quickly.',
  },
  {
    target: 'theme-toggle',
    icon: IoColorPaletteSharp,
    fallback: '🎨',
    title: 'Theme Switch',
    desc: (
      <>
        Prefer a different look? Toggle between light and dark themes here to suit your preference.
        <div className="wt-shortcut-group">
          <ShortcutKey keys={['Shift', 'T']} action={() => document.querySelector('[data-tour="theme-toggle"]')?.click()} />
          <span>Toggle Theme</span>
        </div>
      </>
    ),
  },
  {
    target: 'sidebar-collapse',
    icon: TbLayoutSidebarLeftCollapse,
    fallback: '↔️',
    title: 'Collapse Sidebar',
    desc: (
      <>
        Need more workspace? Collapse the sidebar to minimize it and gain extra screen real-estate.
        <div className="wt-shortcut-group">
          <ShortcutKey keys={['Shift', 'C']} action={() => document.querySelector('[data-tour="sidebar-collapse"]')?.click()} />
          <span>Collapse Sidebar</span>
        </div>
      </>
    ),
  },
  {
    target: 'dropdown-menu',
    icon: MdArrowDropDownCircle,
    fallback: '📂',
    title: 'Dropdown Menus',
    desc: 'Navigation groups can be expanded or collapsed. Click on a group header to reveal its items and explore more options.',
  },
  {
    target: 'account-settings',
    icon: RiUserSettingsFill,
    fallback: '⚙️',
    title: 'Account Settings',
    desc: 'Manage your account, chat, privacy settings, and more from this section in the sidebar.',
  },
  {
    target: 'logout',
    icon: IoLogOut,
    fallback: '🚪',
    title: 'Logout',
    desc: (
      <>
        When you're done, click here to safely log out of your session.
        <div className="wt-shortcut-group">
          <ShortcutKey keys={['Shift', 'L']} action={() => document.querySelector('[data-tour="logout"]')?.click()} />
          <span>Logout Now</span>
        </div>
      </>
    ),
  },
  {
    target: null,
    icon: BsKeyboard,
    fallback: '⌨️',
    title: 'Keyboard Shortcuts',
    desc: (
      <>
        You can navigate this entire platform using keyboard shortcuts!
        <div className="wt-shortcut-group">
          <ShortcutKey keys={['Shift', 'I']} action={() => document.querySelector('[data-tour="instructions-link"]')?.click()} />
          <span>See all Shortcuts</span>
        </div>
      </>
    ),
  },
  {
    target: null,
    icon: MdCelebration,
    fallback: '🎉',
    title: "You're All Set!",
    desc: 'Thanks for taking the tour! We hope you enjoy using the platform. If you ever need a refresher, just click the help button in the bottom-right corner.',
  },
];

const LS_KEY = 'walkthrough-completed';
const PADDING = 10; // px around the highlighted element

/* ═══════════════════════════════════════════════════════════ */
const WalkthroughTour = () => {
  const [isActive, setIsActive] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null); // target bounding rect
  const [modalPos, setModalPos] = useState(null); // { top, left }
  const [arrowStyle, setArrowStyle] = useState(null);
  const [arrowDir, setArrowDir] = useState('down');
  const overlayRef = useRef(null);
  const tickRef = useRef(null);

  /* ── Auto‑open on first visit ────────────────────────── */
  useEffect(() => {
    if (!localStorage.getItem(LS_KEY)) {
      // small delay so the sidebar renders first
      const id = setTimeout(() => setIsActive(true), 800);
      return () => clearTimeout(id);
    }
  }, []);

  /* ── Scrolling Stop when Walkthrough is open ────────────────────────── */
  useEffect(() => {
    if (isActive) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isActive]);
  /* ── Measure target + position modal ─────────────────── */
  const measure = useCallback(() => {
    const { target } = STEPS[step];
    if (!target) {
      setRect(null);
      setModalPos(null);
      setArrowStyle(null);
      return;
    }

    const el = document.querySelector(`[data-tour="${target}"]`);
    if (!el) {
      setRect(null);
      setModalPos(null);
      setArrowStyle(null);
      return;
    }

    const r = el.getBoundingClientRect();

    /* For main-content, clip the left edge to the sidebar boundary */
    let adjLeft = r.left;
    let adjWidth = r.width;
    if (target === 'main-content') {
      const sidebar = document.querySelector('[data-tour="sidebar-nav"]');
      if (sidebar) {
        const sidebarRight = sidebar.getBoundingClientRect().right;
        if (adjLeft < sidebarRight) {
          adjWidth -= sidebarRight - adjLeft;
          adjLeft = sidebarRight;
        }
      }
    }

    const spotRect = {
      top: r.top - PADDING,
      left: adjLeft - PADDING,
      width: adjWidth + PADDING * 2,
      height: r.height + PADDING * 2,
    };
    setRect(spotRect);

    /* Decide where to place the modal relative to the element */
    const modalW = Math.min(420, window.innerWidth - 32);
    const modalH = 260; // approximate including shortcut buttons
    const GAP = 22;

    let mTop, mLeft, aDir;

    // Custom alignment for specific targets
    if (target === 'sidebar-nav' || target === 'user-identity' || target === 'sidebar-collapse' || target === 'theme-toggle' || target === 'instructions-link' || target === 'account-settings' || target === 'logout') {
      // Place to the right of sidebar elements
      mTop = Math.max(16, r.top + r.height / 2 - modalH / 2);
      mLeft = r.right + GAP;
      aDir = 'left';
    } else if (target === 'main-content') {
      // Place inside main content but centered vertically
      mTop = window.innerHeight / 2 - modalH / 2;
      mLeft = adjLeft + 40;
      aDir = 'left';
    } else if (r.bottom + GAP + modalH < window.innerHeight) {
      // Place below
      mTop = r.bottom + GAP;
      mLeft = Math.max(16, Math.min(r.left + r.width / 2 - modalW / 2, window.innerWidth - modalW - 16));
      aDir = 'up';
    } else if (r.top - GAP - modalH > 0) {
      // Place above
      mTop = r.top - GAP - modalH;
      mLeft = Math.max(16, Math.min(r.left + r.width / 2 - modalW / 2, window.innerWidth - modalW - 16));
      aDir = 'down';
    } else {
      // Fallback: Place to the right
      mTop = Math.max(16, r.top + r.height / 2 - modalH / 2);
      mLeft = r.right + GAP;
      aDir = 'left';
    }

    /* Clamp modal so it doesn't overflow past viewport bottom or right */
    mTop = Math.min(mTop, window.innerHeight - modalH - 24);
    mTop = Math.max(24, mTop);
    mLeft = Math.min(mLeft, window.innerWidth - modalW - 24);
    mLeft = Math.max(24, mLeft);

    setModalPos({ top: mTop, left: mLeft });
    setArrowDir(aDir);

    // Position the arrow between element and modal
    const arrowPos = {};
    const arrowCenterLeft = target === 'main-content' ? adjLeft + adjWidth / 2 : r.left + r.width / 2;
    const arrowCenterTop = r.top + r.height / 2;

    if (aDir === 'up') {
      arrowPos.top = mTop - 14;
      arrowPos.left = Math.max(mLeft + 20, Math.min(mLeft + modalW - 20, arrowCenterLeft - 10));
    } else if (aDir === 'down') {
      arrowPos.top = mTop + modalH;
      arrowPos.left = Math.max(mLeft + 20, Math.min(mLeft + modalW - 20, arrowCenterLeft - 10));
    } else if (aDir === 'left') {
      arrowPos.top = Math.max(mTop + 20, Math.min(mTop + modalH - 20, arrowCenterTop - 10));
      arrowPos.left = mLeft - 14;
    } else {
      arrowPos.top = Math.max(mTop + 20, Math.min(mTop + modalH - 20, arrowCenterTop - 10));
      arrowPos.left = mLeft + modalW;
    }
    setArrowStyle(arrowPos);
  }, [step]);

  useEffect(() => {
    if (!isActive) return;
    measure();
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    // Re‑measure periodically in case of layout shifts (sidebar animations)
    tickRef.current = setInterval(measure, 600);
    return () => {
      window.removeEventListener('resize', onResize);
      clearInterval(tickRef.current);
    };
  }, [isActive, measure]);

  /* ── Handlers ────────────────────────────────────────── */
  const finish = useCallback(() => {
    setIsActive(false);
    setStep(0);
    localStorage.setItem(LS_KEY, 'true');
  }, []);

  const next = useCallback(() => {
    if (step < STEPS.length - 1) setStep((s) => s + 1);
    else finish();
  }, [step, finish]);

  const prev = useCallback(() => {
    if (step > 0) setStep((s) => s - 1);
  }, [step]);

  const restart = useCallback(() => {
    setStep(0);
    setIsActive(true);
  }, []);

  /* ── Keyboard navigation (left / right arrows + Shortcuts) ────── */
  useEffect(() => {
    const onKey = (e) => {
      // Navigation
      if (isActive) {
        if (e.key === 'ArrowRight') next();
        else if (e.key === 'ArrowLeft') {
          if (step > 0) prev();
          else finish();
        } else if (e.key === 'Escape') finish();
      }

      // Global Shortcuts (Even when tour is inactive/active)
      // Only trigger if Shift is the ONLY modifier pressed
      // PREVENT global shortcuts when typing in inputs/textareas (where Shift is used for capitalization)
      const target = e.target;
      const isInput = target.tagName === 'Input' || target.tagName === 'Textarea' || 
                      target.isContentEditable || target.closest('.form-control');

      if (e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey && !isInput) {
        const key = e.key.toUpperCase();
        if (key === 'W') {
          e.preventDefault();
          restart();
        } else if (key === 'I') {
          e.preventDefault();
          document.querySelector('[data-tour="instructions-link"]')?.click();
        } else if (key === 'S') {
          e.preventDefault();
          document.querySelector('.sidebar-search-input')?.focus();
        } else if (key === 'C') {
          e.preventDefault();
          document.querySelector('[data-tour="sidebar-collapse"]')?.click();
        } else if (key === 'T') {
          e.preventDefault();
          document.querySelector('[data-tour="theme-toggle"]')?.click();
        } else if (key === 'L') {
          e.preventDefault();
          document.querySelector('[data-tour="logout"]')?.click();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isActive, step, next, prev, finish, restart]);

  const renderIcon = (Icon, fallback) => {
    if (!navigator.onLine || !Icon) {
      return <span>{fallback}</span>;
    }
    return <Icon />;
  };

  /* ── Render ──────────────────────────────────────────── */
  const currentStep = STEPS[step];
  const isCentered = !currentStep.target;
  const progress = (step / (STEPS.length - 1)) * 100;

  return (
    <>
      {/* ── Floating restart button (always visible) ──── */}
      <button
        className="wt-fab"
        onClick={restart}
        aria-label="Start walkthrough"
        data-tour="restart-btn"
        title="Replay Walkthrough"
      >
        {renderIcon(IoFootsteps, '?')}
        <span className="wt-fab__tooltip">Replay Walkthrough</span>
      </button>

      {isActive && (
        <>
          {/* ── Overlay with spotlight cutout ──────── */}
          <div className="wt-overlay" ref={overlayRef}>
            <svg className="wt-overlay__backdrop" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <mask id="wt-mask">
                  <rect x="0" y="0" width="100%" height="100%" fill="white" />
                  {rect && (
                    <rect
                      x={rect.left}
                      y={rect.top}
                      width={rect.width}
                      height={rect.height}
                      rx="12"
                      fill="black"
                    />
                  )}
                </mask>
              </defs>
              <rect
                x="0"
                y="0"
                width="100%"
                height="100%"
                fill="rgba(0,0,0,0.55)"
                mask="url(#wt-mask)"
              />
            </svg>
          </div>

          {/* ── Spotlight ring ─────────────────────── */}
          {rect && (
            <div
              className={`wt-spotlight ${!rect ? 'wt-spotlight--hidden' : ''}`}
              style={{
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
              }}
            />
          )}

          {/* ── Arrow ──────────────────────────────── */}
          {arrowStyle && !isCentered && (
            <div
              className={`wt-arrow wt-arrow--${arrowDir}`}
              style={{ top: arrowStyle.top, left: arrowStyle.left }}
            />
          )}

          {/* ── Modal card ─────────────────────────── */}
          <div
            className={`wt-modal ${isCentered ? 'wt-modal--center' : ''}`}
            style={!isCentered && modalPos ? { top: modalPos.top, left: modalPos.left } : undefined}
            key={step} /* re‑trigger enter animation */
          >
            {/* Close */}
            <button className="wt-modal__close" onClick={finish} aria-label="Close walkthrough">
              {/* ✕ */}
              {renderIcon(RxCross2, '✕')}
            </button>

            {/* Content */}
            <div className="wt-modal__header">
              <span className="wt-modal__icon">
                {renderIcon(currentStep.icon, currentStep.fallback)}
              </span>
              <h3 className="wt-modal__title">{currentStep.title}</h3>
            </div>
            <p className="wt-modal__desc">{currentStep.desc}</p>

            {/* Progress bar */}
            <div className="wt-modal__progress-track">
              <div className="wt-modal__progress-bar" style={{ width: `${progress}%` }} />
            </div>

            {/* Footer */}
            <div className="wt-modal__footer">
              {step > 0 ? (
                <button className="wt-modal__btn wt-modal__btn--secondary" onClick={prev}>
                  ← Previous
                </button>
              ) : (
                <button className="wt-modal__btn wt-modal__btn--secondary" onClick={finish}>
                  Skip
                </button>
              )}

              <span className="wt-modal__counter">
                Step {step} of {STEPS.length - 1}
              </span>

              <button className="wt-modal__btn wt-modal__btn--primary" onClick={next}>
                {step === STEPS.length - 1 ? 'Finish ✓' : 'Next →'}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default WalkthroughTour;

